import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Grid, Stack, Typography, CircularProgress, IconButton, Tooltip, Tabs, Tab } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector, useDispatch } from 'react-redux';
import { generateClient } from 'aws-amplify/api';
import { organizationState } from 'redux/organizationSlice';
import { clientActions } from 'redux/clientSlice';
import { listCERTIFICATES } from 'graphql/queries';
import EmailMetricCards from './EmailMetricCards';
import EmailOverviewChart from './EmailOverviewChart';
import VendorEmailTable from './VendorEmailTable';
import EmailActivityFilters from './EmailActivityFilters';
import EmailPulse from './EmailPulse';

const API = generateClient();

export default function EmailActivity() {
  const dispatch = useDispatch();
  const { selectedClient } = useSelector(organizationState);
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (selectedClient?.id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [selectedClient, lastRefresh]);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('Fetching data for client:', selectedClient);
      
      // Fetch certificates
      const certResult = await dispatch(clientActions.listClientCertificates({
        clientID: selectedClient.clientID
      }));
      
      // Fetch vendors
      const vendorResult = await dispatch(clientActions.listClientVendorsForClient({
        clientID: selectedClient.clientID
      }));
      
      console.log('Result from listClientCertificates:', certResult);
      console.log('Result from listClientVendorsForClient:', vendorResult);
      
      // Set certificates
      if (certResult.payload?.certificates) {
        console.log('Fetched certificates:', certResult.payload.certificates);
        console.log('Certificates with sentTo:', certResult.payload.certificates.filter(c => c.sentTo && c.sentTo.length > 0));
        setCertificates(certResult.payload.certificates);
      } else if (certResult.payload?.items) {
        console.log('Fetched items:', certResult.payload.items);
        console.log('Items with sentTo:', certResult.payload.items.filter(c => c.sentTo && c.sentTo.length > 0));
        setCertificates(certResult.payload.items);
      } else {
        console.log('No certificates found in payload');
        setCertificates([]);
      }
      
      // Set vendors
      if (vendorResult.payload?.clientVendors) {
        console.log('Fetched vendors:', vendorResult.payload.clientVendors);
        setVendors(vendorResult.payload.clientVendors);
      } else {
        console.log('No vendors found in payload');
        setVendors([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter certificates based on date range
  const filteredCertificates = useMemo(() => {
    if (!dateFilter || dateFilter === 'all') {
      return certificates;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;

    switch (dateFilter) {
      case 'today':
        startDate = today;
        break;
      case '7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        break;
      default:
        return certificates;
    }

    console.log(`Filtering certificates for date range: ${dateFilter}, start date: ${startDate}`);
    
    const filtered = certificates.filter(cert => {
      // Check multiple date fields for filtering
      const certDate = cert.createdAt || cert.certificateDate || cert.updatedAt;
      if (!certDate) return false;
      
      const date = new Date(certDate);
      return date >= startDate;
    });
    
    console.log(`Filtered ${filtered.length} certificates from ${certificates.length} total`);
    return filtered;
  }, [certificates, dateFilter]);

  // Calculate email metrics from certificate data
  const emailMetrics = useMemo(() => {
    const metrics = {
      totalSent: 0,
      totalOpened: 0,
      totalUnopened: 0,
      uniqueRecipients: new Set(),
      openRate: 0,
      vendorMetrics: new Map()
    };

    // Create vendor lookup map
    const vendorLookup = new Map();
    vendors.forEach(cv => {
      if (cv.id && cv.vendor) {
        vendorLookup.set(cv.id, cv.vendor);
      }
    });

    filteredCertificates.forEach(cert => {
      if (cert.sentTo && cert.sentTo.length > 0) {
        const sentCount = cert.sentTo.length;
        const openedCount = cert.readBy ? cert.readBy.length : 0;
        
        metrics.totalSent += sentCount;
        metrics.totalOpened += openedCount;
        
        // Add unique recipients
        cert.sentTo.forEach(email => metrics.uniqueRecipients.add(email));
        
        // Get vendor information from lookup
        const vendorId = cert.clientVendorID;
        const vendor = vendorLookup.get(vendorId);
        
        console.log('Certificate vendor mapping:', {
          certId: cert.id,
          clientVendorID: vendorId,
          vendor: vendor,
          sentTo: cert.sentTo
        });
        
        if (vendorId && vendor) {
          const vendorName = vendor.name || vendor.vendorName || 'Unknown Vendor';
          
          if (!metrics.vendorMetrics.has(vendorId)) {
            metrics.vendorMetrics.set(vendorId, {
              vendorId,
              vendorName,
              sent: 0,
              opened: 0,
              certificates: 0
            });
          }
          
          const vendorData = metrics.vendorMetrics.get(vendorId);
          vendorData.sent += sentCount;
          vendorData.opened += openedCount;
          vendorData.certificates += 1;
        } else {
          console.log('No vendor found for certificate:', cert.id, 'with clientVendorID:', vendorId);
        }
      }
    });

    metrics.totalUnopened = metrics.totalSent - metrics.totalOpened;
    metrics.openRate = metrics.totalSent > 0 
      ? ((metrics.totalOpened / metrics.totalSent) * 100).toFixed(1) 
      : 0;

    return metrics;
  }, [filteredCertificates, vendors]);

  if (!selectedClient) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography variant="h6" color="textSecondary">
          Please select a client to view email activity
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleRefresh = () => {
    setLastRefresh(Date.now());
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box padding={3}>
      <Stack spacing={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">Email Activity</Typography>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Tabs */}
        <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="email activity tabs">
            <Tab label="Email Activity" />
            <Tab label="Email Pulse" />
            <Tab label="Vendor Email Performance" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {/* Tab 1: Email Activity - Charts and Metrics */}
        {activeTab === 0 && (
          <>
            {/* Date Filter */}
            <EmailActivityFilters 
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
            />

            {/* Metric Cards */}
            <EmailMetricCards metrics={emailMetrics} />

            {/* Charts */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper>
                  <EmailOverviewChart metrics={emailMetrics} />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper>
                  <Box padding={2}>
                    <Typography variant="h6" gutterBottom>
                      Email Engagement Summary
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 3 }}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Total Recipients
                        </Typography>
                        <Typography variant="h4">
                          {emailMetrics.uniqueRecipients.size}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Active Vendors
                        </Typography>
                        <Typography variant="h4">
                          {emailMetrics.vendorMetrics.size}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Certificates with Email Activity
                        </Typography>
                        <Typography variant="h4">
                          {filteredCertificates.filter(c => c.sentTo && c.sentTo.length > 0).length}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {/* Tab 2: Email Pulse - Sent and Upcoming Emails */}
        {activeTab === 1 && (
          <EmailPulse 
            certificates={certificates}
            vendors={vendors}
            selectedClient={selectedClient}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />
        )}

        {/* Tab 3: Vendor Email Performance - Table Only */}
        {activeTab === 2 && (
          <>
            {/* Date Filter */}
            <EmailActivityFilters 
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
            />
            
            {/* Vendor Table */}
            <Paper>
              <VendorEmailTable vendorMetrics={Array.from(emailMetrics.vendorMetrics.values())} />
            </Paper>
          </>
        )}
      </Stack>
    </Box>
  );
}